import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { AdminDailyLogEditor } from './AdminDailyLogEditor';
import { LeaderboardMonthPicker } from './LeaderboardMonthPicker';
import { 
  Trophy,
  Users,
  Calendar as CalendarIcon,
  Target,
  Clock,
  Flame,
  Dumbbell,
  ChevronRight,
  Check,
  Droplet,
  Timer,
  Award,
  Pencil,
  Trash2,
  X,
  Loader2,
  Save,
  UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { getImageUrl } from '../../utils/imageUtils';

const API = process.env.REACT_APP_BACKEND_URL;

// Challenge type icons
const CHALLENGE_TYPE_CONFIG = {
  workout_count: { icon: Dumbbell, label: 'Complete Workouts', unit: 'workouts' },
  total_minutes: { icon: Clock, label: 'Total Minutes', unit: 'minutes' },
  total_calories: { icon: Flame, label: 'Burn Calories', unit: 'calories' },
  streak: { icon: Calendar, label: 'Day Streak', unit: 'days' },
  specific_workout: { icon: Target, label: 'Category Challenge', unit: 'workouts' },
  daily_water: { icon: Droplet, label: 'Hydration', unit: 'oz' },
  daily_reps: { icon: Dumbbell, label: 'Daily Reps', unit: 'reps' },
  daily_duration: { icon: Timer, label: 'Daily Duration', unit: 'sec' }
};

export const ChallengeCard = ({ challenge, api, onJoin, onLeave, isAdmin = false, onChanged }) => {
  const [joining, setJoining] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const config = CHALLENGE_TYPE_CONFIG[challenge.challenge_type] || CHALLENGE_TYPE_CONFIG.workout_count;
  const ChallengeIcon = config.icon;
  
  const isActive = new Date(challenge.start_date) <= new Date() && new Date(challenge.end_date) >= new Date();
  const hasEnded = new Date(challenge.end_date) < new Date();
  const goal = challenge.user_target || challenge.target_value;
  const progressPct = goal > 0 
    ? Math.min(100, (challenge.user_progress / goal) * 100) 
    : 0;

  const handleJoin = async () => {
    setJoining(true);
    try {
      await api().post(`/challenges/${challenge.id}/join`);
      const days = getDaysRemaining();
      // Friendly "you've still got time" toast — affirms late joiners that
      // the goal is reachable. Different copy on day 1 vs the final stretch.
      const msg = days <= 1
        ? `Joined! You've got 1 day — go all-in! 🔥`
        : days <= 3
          ? `Joined! ${days} days to hit the goal — push it! 💪`
          : days <= 7
            ? `Joined! ${days} days left — totally doable. Let's go! 🚀`
            : `Joined! You have ${days} days to crush this goal. 💯`;
      toast.success(msg);
      if (onJoin) onJoin(challenge.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join challenge');
    }
    setJoining(false);
  };

  const handleRestart = async () => {
    try {
      await api().post(`/challenges/${challenge.id}/restart`);
      toast.success('Fresh start! Your lifetime total is saved. 💪');
      if (onChanged) onChanged(); else if (onJoin) onJoin(challenge.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not restart');
    }
  };

  const handleLeave = async () => {
    const ok = window.confirm(
      `Leave "${challenge.title}"? Your progress on this challenge will be lost and you'll have to start over if you re-join.`
    );
    if (!ok) return;
    try {
      await api().post(`/challenges/${challenge.id}/leave`);
      toast.success('Left challenge');
      if (onLeave) onLeave(challenge.id);
    } catch (err) {
      toast.error('Failed to leave challenge');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${challenge.title}"? This removes the challenge for everyone and cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api().delete(`/challenges/${challenge.id}`);
      toast.success('Challenge deleted');
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete challenge');
      setDeleting(false);
    }
  };

  const getDaysRemaining = () => {
    const end = new Date(challenge.end_date);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  // For active challenges show a visible "Ends in X days" pill — drives the
  // monthly urgency. Color ramps as the deadline approaches.
  const daysLeft = isActive ? getDaysRemaining() : null;
  const urgencyClass = daysLeft == null
    ? ''
    : daysLeft <= 3
      ? 'bg-red-500/20 text-red-400 border-red-500/40'
      : daysLeft <= 7
        ? 'bg-orange-500/20 text-orange-400 border-orange-500/40'
        : 'bg-primary/20 text-primary border-primary/40';

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className={`bg-card border rounded-2xl card-hover overflow-hidden ${
        challenge.user_participating ? 'border-primary' : 'border-border'
      }`}
    >
      {/* Challenge Image/Header */}
      <div className="relative h-32 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
        <ChallengeIcon className="h-16 w-16 text-primary/50" />
        
        {/* Status Badge */}
        <div className="absolute top-3 right-3 flex gap-2">
          {challenge.milestones?.length > 0 && (
            <Badge className="bg-yellow-500/20 text-yellow-400">
              <Award className="h-3 w-3 mr-1" />
              {challenge.milestones.length} Milestones
            </Badge>
          )}
          {challenge.is_team_challenge && (
            <Badge className="bg-blue-500/20 text-blue-400">
              <Users className="h-3 w-3 mr-1" />
              Team
            </Badge>
          )}
          {hasEnded ? (
            <Badge className="bg-gray-500/20 text-gray-400">Ended</Badge>
          ) : isActive ? (
            <Badge
              className={`${urgencyClass} border font-semibold`}
              data-testid={`challenge-countdown-${challenge.id}`}
              title={`Ends ${new Date(challenge.end_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
            >
              <Clock className="h-3 w-3 mr-1" />
              {daysLeft === 0 ? 'Ends today!' : daysLeft === 1 ? 'Ends tomorrow' : `${daysLeft} days left`}
            </Badge>
          ) : (
            <Badge className="bg-yellow-500/20 text-yellow-400">Upcoming</Badge>
          )}
        </div>
        
        {challenge.user_participating && challenge.completed && (
          <div className="absolute top-3 left-3">
            <Badge className="bg-primary text-black">
              <Check className="h-3 w-3 mr-1" />
              Completed!
            </Badge>
          </div>
        )}

        {/* Admin-only edit / delete controls — visible only to admins so
            non-admins never see them. Backend already enforces admin on
            PUT/DELETE so these are safe to hide/show client-side. */}
        {isAdmin && (
          <div className="absolute top-3 left-3 flex gap-1">
            <button
              onClick={() => setEditOpen(true)}
              className="h-8 w-8 rounded-lg bg-black/70 hover:bg-black border border-primary/40 hover:border-primary text-primary flex items-center justify-center transition-colors"
              data-testid={`admin-edit-challenge-${challenge.id}`}
              title="Edit challenge"
              aria-label="Edit challenge"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="h-8 w-8 rounded-lg bg-black/70 hover:bg-red-500/20 border border-red-500/40 hover:border-red-400 text-red-400 flex items-center justify-center transition-colors disabled:opacity-50"
              data-testid={`admin-delete-challenge-${challenge.id}`}
              title="Delete challenge"
              aria-label="Delete challenge"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            </button>
          </div>
        )}
      </div>

      <div className="p-4">
        {/* Title & Description */}
        <h3 className="font-heading text-lg text-white uppercase tracking-wider mb-1">
          {challenge.title}
        </h3>
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
          {challenge.description}
        </p>

        {/* Target */}
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-2">
            <ChallengeIcon className="h-4 w-4 text-primary" />
            <span className="text-white font-medium">{challenge.target_value} {config.unit}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="h-4 w-4" />
            <span>{challenge.participant_count} joined</span>
          </div>
        </div>

        {/* Progress (if participating) */}
        {challenge.user_participating && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-muted-foreground">Your Progress</span>
              <span className="text-primary font-medium">
                {challenge.user_progress} / {goal}
              </span>
            </div>
            {challenge.is_personal_target && (
              <p className="text-[11px] text-white/50 mb-1" data-testid={`challenge-personal-goal-${challenge.id}`}>
                Your goal: {goal.toLocaleString()} (joined {new Date(challenge.joined_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})
              </p>
            )}
            <Progress value={progressPct} className="h-2" />
          </div>
        )}

        {/* Time Remaining — replaced by the prominent countdown pill at top-right
            of the card image. Keeping a low-key end-date label only: e.g. "Ends Apr 30". */}
        {isActive && !hasEnded && (
          <div className="text-sm text-muted-foreground mb-3">
            <CalendarIcon className="h-4 w-4 inline mr-1" />
            Ends {new Date(challenge.end_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2">
          {!hasEnded && !challenge.user_participating && (
            <Button
              onClick={handleJoin}
              disabled={joining}
              className="w-full h-11 rounded-xl bg-primary text-black font-heading uppercase tracking-wider hover:bg-yellow-300"
              data-testid={`join-challenge-btn-${challenge.id}`}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              {joining ? 'Joining...' : 'Join Challenge'}
            </Button>
          )}
          {!hasEnded && challenge.user_participating && challenge.completed && (
            <Button
              onClick={handleRestart}
              className="w-full h-11 rounded-xl bg-green-500 text-black font-heading uppercase tracking-wider hover:bg-green-400"
              data-testid={`restart-challenge-btn-${challenge.id}`}
            >
              Start again
            </Button>
          )}
          {hasEnded && (
            <div className="w-full h-11 rounded-xl border border-white/10 text-white/50 text-sm flex items-center justify-center" data-testid={`challenge-ended-note-${challenge.id}`}>
              Ended. Restarting soon
            </div>
          )}
          <div className="flex gap-2">
            <Link to={`/challenges/${challenge.id}`} className="flex-1">
              <Button variant="outline" className="w-full rounded-xl" data-testid={`view-challenge-btn-${challenge.id}`}>
                View Details
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
            {!hasEnded && challenge.user_participating && (
              <Button
                variant="outline"
                onClick={handleLeave}
                className="rounded-xl text-red-400 border-red-400/50 hover:bg-red-500/10"
                data-testid={`leave-challenge-btn-${challenge.id}`}
              >
                Leave
              </Button>
            )}
          </div>
        </div>
      </div>

      <EditChallengeModal
        key={editOpen ? `edit-${challenge.id}` : 'closed'}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        challenge={challenge}
        api={api}
        onSaved={() => {
          setEditOpen(false);
          if (onChanged) onChanged();
        }}
      />
    </motion.div>
  );
};

/**
 * Admin-only modal to edit a challenge's core fields (title, description,
 * target, end date). Posts to PUT /api/challenges/{id}. Backend only allows
 * these whitelisted fields so admins can't accidentally corrupt the schema.
 */
const EditChallengeModal = ({ open, onClose, challenge, api, onSaved }) => {
  const [form, setForm] = useState({
    title: challenge.title || '',
    description: challenge.description || '',
    target_value: challenge.target_value || 0,
    daily_target: challenge.daily_target || 0,
    end_date: challenge.end_date ? challenge.end_date.slice(0, 10) : '',
    is_public: challenge.is_public !== false,
  });
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const save = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      // Build payload — only include fields the backend whitelists.
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        target_value: Number(form.target_value) || 0,
        daily_target: Number(form.daily_target) || 0,
        is_public: !!form.is_public,
      };
      if (form.end_date) {
        // Preserve end-of-day semantics so "ends today" = 23:59:59 UTC
        const endIso = new Date(`${form.end_date}T23:59:59.999Z`).toISOString();
        payload.end_date = endIso;
      }
      await api().put(`/challenges/${challenge.id}`, payload);
      toast.success('Challenge updated');
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
    setSaving(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.96, y: 10 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.96, y: 10 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-card border border-primary/40 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-[0_0_48px_rgba(255,215,0,0.15)]"
          data-testid="edit-challenge-modal"
        >
          {/* Header (sticky) */}
          <div className="flex items-center justify-between p-4 pb-3 border-b border-border/60">
            <div className="flex items-center gap-2">
              <Pencil className="h-4 w-4 text-primary" />
              <h3 className="font-heading text-base text-white uppercase tracking-wider">Edit Challenge</h3>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-white"
              aria-label="close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Scrollable body — action buttons live at the end so they're
              always inline with the form content. */}
          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
            <div>
              <Label className="text-muted-foreground text-[10px] uppercase tracking-wider">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 bg-black border-border text-white h-9 text-sm"
                data-testid="edit-challenge-title"
              />
            </div>
            <div>
              <Label className="text-muted-foreground text-[10px] uppercase tracking-wider">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1 bg-black border-border text-white min-h-[60px] text-sm"
                data-testid="edit-challenge-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-muted-foreground text-[10px] uppercase tracking-wider">Target</Label>
                <Input
                  type="number"
                  value={form.target_value}
                  onChange={(e) => setForm({ ...form, target_value: e.target.value })}
                  className="mt-1 bg-black border-border text-white h-9 text-sm"
                  data-testid="edit-challenge-target"
                />
              </div>
              <div>
                <Label className="text-muted-foreground text-[10px] uppercase tracking-wider">Daily Target</Label>
                <Input
                  type="number"
                  value={form.daily_target}
                  onChange={(e) => setForm({ ...form, daily_target: e.target.value })}
                  className="mt-1 bg-black border-border text-white h-9 text-sm"
                  data-testid="edit-challenge-daily-target"
                  placeholder="reps/day"
                />
              </div>
            </div>

            {/* Themed end-date picker — same Popover+Calendar combo as the
                SetLogger so we never trigger the native green Android picker. */}
            <div>
              <Label className="text-muted-foreground text-[10px] uppercase tracking-wider">End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="w-full mt-1 h-9 flex items-center gap-2 bg-black border border-border rounded-md px-3 text-white text-sm text-left hover:border-primary/60 transition-colors"
                    data-testid="edit-challenge-end-trigger"
                  >
                    <CalendarIcon className="h-3.5 w-3.5 text-primary shrink-0" />
                    <span>
                      {form.end_date
                        ? format(new Date(`${form.end_date}T00:00:00`), 'MMM d, yyyy')
                        : 'Pick a date'}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="p-0 bg-card border border-primary/40 rounded-xl shadow-[0_0_32px_rgba(255,215,0,0.18)]"
                >
                  <Calendar
                    mode="single"
                    selected={form.end_date ? new Date(`${form.end_date}T00:00:00`) : undefined}
                    onSelect={(d) => {
                      if (!d) return;
                      const m = String(d.getMonth() + 1).padStart(2, '0');
                      const dd = String(d.getDate()).padStart(2, '0');
                      setForm({ ...form, end_date: `${d.getFullYear()}-${m}-${dd}` });
                    }}
                    initialFocus
                    className="bg-card text-white"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <label className="flex items-center gap-2 text-white text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.is_public}
                onChange={(e) => setForm({ ...form, is_public: e.target.checked })}
                className="h-4 w-4 rounded accent-primary"
              />
              Public (visible to all users)
            </label>

            {/* Action buttons — full-width Save always visible right under the
                form so it's impossible to miss on any viewport. */}
            <div className="pt-2 space-y-2">
              <Button
                onClick={save}
                disabled={saving}
                className="w-full h-11 bg-green-500 hover:bg-green-400 active:bg-green-600 text-white font-heading tracking-wider shadow-[0_0_16px_rgba(34,197,94,0.45)] disabled:opacity-60"
                data-testid="save-challenge-btn"
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" /> Save Changes</>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={onClose}
                disabled={saving}
                className="w-full h-9"
                data-testid="cancel-challenge-edit"
              >
                Cancel
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export const ChallengeLeaderboard = ({ leaderboard, challenge, currentUserId, isAdmin = false, api, onChanged, availableMonths = [], initialMonth = null }) => {
  const [resettingId, setResettingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draftValue, setDraftValue] = useState('');
  const [savingId, setSavingId] = useState(null);
  // The user whose daily-log editor modal is currently open (null when closed).
  const [logEditorUser, setLogEditorUser] = useState(null);

  // Currently selected month in the leaderboard header dropdown. Defaults to
  // the current cycle month passed from the parent, falling back to the
  // newest available month or today's YYYY-MM.
  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(initialMonth || currentMonthKey);
  // Locally-scoped leaderboard — starts as the current-month leaderboard
  // from the parent, gets replaced when the user picks a different month.
  const [scopedLeaderboard, setScopedLeaderboard] = useState(leaderboard || []);
  const [loadingMonth, setLoadingMonth] = useState(false);

  // Keep local leaderboard in sync with parent when its prop changes
  // (e.g. after a rep is logged and parent re-fetches).
  useEffect(() => {
    if (selectedMonth === (initialMonth || currentMonthKey)) {
      setScopedLeaderboard(leaderboard || []);
    }
  }, [leaderboard, initialMonth, selectedMonth, currentMonthKey]);

  const handleMonthChange = async (monthKey) => {
    setSelectedMonth(monthKey);
    if (monthKey === (initialMonth || currentMonthKey)) {
      // Snap back to the parent's current-month leaderboard
      setScopedLeaderboard(leaderboard || []);
      return;
    }
    if (!api) return;
    setLoadingMonth(true);
    try {
      const res = await api().get(`/challenges/${challenge.id}?month=${monthKey}`);
      setScopedLeaderboard(res.data.leaderboard || []);
    } catch (err) {
      // If anything fails, keep the current view but let the user know.
      toast.error('Could not load that month');
    } finally {
      setLoadingMonth(false);
    }
  };

  // Human-readable month label ("July 2026" for "2026-07")
  const monthLabel = (key) => {
    if (!key || !/^\d{4}-\d{2}$/.test(key)) return key || '';
    const [y, m] = key.split('-');
    const date = new Date(Number(y), Number(m) - 1, 1);
    return date.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  };

  const isCurrentMonth = selectedMonth === (initialMonth || currentMonthKey);

  // Daily-log editing only makes sense for log-daily style challenges where
  // a per-day breakdown actually exists. For workout_count / total_calories
  // challenges current_value is computed from workouts, not a date series.
  const supportsDailyLogs = ['daily_reps', 'daily_water', 'daily_duration'].includes(challenge?.challenge_type);

  const setProgress = async (user, value) => {
    setSavingId(user.id);
    try {
      await api().post(
        `/challenges/${challenge.id}/admin/reset-participant/${user.id}`,
        { current_value: value }
      );
      toast.success(`${user.name} set to ${value}`);
      setEditingId(null);
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update');
    } finally {
      setSavingId(null);
    }
  };

  const handleReset = async (user) => {
    if (!window.confirm(`Drop ${user.name} to 0 for this month?\n\nTheir progress will move into their private Lifetime Total and they'll start fresh on the leaderboard.`)) return;
    setResettingId(user.id);
    try {
      await api().post(`/challenges/${challenge.id}/admin/reset-participant/${user.id}`);
      toast.success(`${user.name} reset to 0 for this month`);
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset');
    } finally {
      setResettingId(null);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.user.id);
    setDraftValue(String(entry.current_value || 0));
  };

  const submitEdit = (entry) => {
    const n = Number(draftValue);
    if (!Number.isFinite(n) || n < 0) {
      toast.error('Enter a number ≥ 0');
      return;
    }
    setProgress(entry.user, Math.floor(n));
  };

  // Display list is the (locally-scoped) leaderboard for the selected month.
  // Ranks are rewritten so #1 sits on top after any re-sort.
  const displayList = (() => {
    const list = (scopedLeaderboard || []).slice();
    list.sort((a, b) => (b.current_value || 0) - (a.current_value || 0));
    return list.map((e, i) => ({ ...e, rank: i + 1 }));
  })();

  return (
    <div className="bg-card border border-border rounded-3xl overflow-hidden">
      <div className="p-4 border-b border-border">
        <h3 className="font-heading text-lg text-white uppercase tracking-wider flex items-center gap-2">
          <Trophy className="h-5 w-5 text-yellow-400" />
          Leaderboard
          <LeaderboardMonthPicker
            months={Array.from(new Set([currentMonthKey, ...(availableMonths || [])])).sort().reverse()}
            value={selectedMonth}
            currentKey={currentMonthKey}
            label={monthLabel}
            onChange={handleMonthChange}
            disabled={loadingMonth}
          />
        </h3>
        <p className="text-[11px] text-muted-foreground mt-1">
          {isCurrentMonth
            ? "Resets on the 1st — only this cycle's reps count toward the rank."
            : `Showing rep totals for ${monthLabel(selectedMonth)}.`}
        </p>
      </div>
      
      <div className="divide-y divide-border">
        {displayList.map((entry) => {
          const isEditing = editingId === entry.user?.id;
          const shownValue = entry.current_value ?? 0;
          return (
          <div 
            key={entry.user?.id}
            className={`p-4 flex items-center gap-4 ${
              entry.user?.id === currentUserId ? 'bg-primary/10' : ''
            }`}
          >
            {/* Rank */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
              entry.rank === 1 ? 'bg-yellow-500 text-black' :
              entry.rank === 2 ? 'bg-gray-400 text-black' :
              entry.rank === 3 ? 'bg-orange-600 text-white' :
              'bg-white/10 text-white'
            }`}>
              {entry.rank}
            </div>
            
            {/* User */}
            <Link to={`/user/${entry.user?.id}`} className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity" data-testid={`leaderboard-user-${entry.rank}`}>
              <Avatar className="h-10 w-10 shrink-0">
                <AvatarImage src={getImageUrl(entry.user?.profile_picture)} />
                <AvatarFallback className="bg-primary/20 text-primary">
                  {entry.user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <span className="text-white font-medium truncate block">{entry.user?.name}</span>
                {entry.completed && (
                  <Badge className="mt-0.5 bg-green-500/20 text-green-400 text-xs">
                    <Check className="h-3 w-3 mr-1" />
                    Done
                  </Badge>
                )}
              </div>
            </Link>
            
            {/* Progress + admin reset / edit */}
            <div className="text-right flex items-center gap-2 shrink-0">
              {isEditing ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    inputMode="numeric"
                    autoFocus
                    value={draftValue}
                    onChange={(e) => setDraftValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submitEdit(entry);
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    disabled={savingId === entry.user.id}
                    className="w-20 bg-black/60 border border-primary/50 rounded-md px-2 py-1 text-white text-sm text-right focus:outline-none focus:border-primary"
                    data-testid={`leaderboard-edit-input-${entry.user.id}`}
                  />
                  <button
                    onClick={() => submitEdit(entry)}
                    disabled={savingId === entry.user.id}
                    className="h-7 w-7 rounded-md bg-green-500/20 hover:bg-green-500/30 border border-green-500/40 text-green-400 flex items-center justify-center disabled:opacity-50"
                    aria-label="Save"
                    data-testid={`leaderboard-edit-save-${entry.user.id}`}
                  >
                    {savingId === entry.user.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    disabled={savingId === entry.user.id}
                    className="h-7 w-7 rounded-md bg-white/5 hover:bg-white/10 text-muted-foreground flex items-center justify-center"
                    aria-label="Cancel"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => isAdmin && isCurrentMonth && startEdit(entry)}
                    className={`text-right ${isAdmin && isCurrentMonth ? 'hover:bg-white/5 rounded px-1.5 py-0.5 -mr-1.5' : 'cursor-default'}`}
                    title={isAdmin && isCurrentMonth ? `Click to edit ${entry.user?.name}'s number` : ''}
                    data-testid={`leaderboard-rep-display-${entry.user?.id}`}
                  >
                    <div className="text-white font-medium flex items-center gap-1 justify-end">
                      {shownValue}
                      {isAdmin && isCurrentMonth && <Pencil className="h-3 w-3 text-muted-foreground" />}
                    </div>
                    <div className="text-xs text-muted-foreground">{isCurrentMonth ? `${entry.progress_percentage}%` : monthLabel(selectedMonth)}</div>
                  </button>
                  {isAdmin && isCurrentMonth && supportsDailyLogs && (
                    <button
                      onClick={() => setLogEditorUser(entry.user)}
                      className="shrink-0 h-7 w-7 rounded-md bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 hover:border-blue-400 text-blue-400 flex items-center justify-center transition-colors"
                      title={`Edit ${entry.user?.name}'s daily log entries`}
                      data-testid={`leaderboard-admin-edit-log-${entry.user?.id}`}
                      aria-label="Edit daily log entries"
                    >
                      <CalendarIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {isAdmin && isCurrentMonth && (entry.current_value || 0) > 0 && (
                    <button
                      onClick={() => handleReset(entry.user)}
                      disabled={resettingId === entry.user?.id}
                      className="shrink-0 h-7 w-7 rounded-md bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 hover:border-red-400 text-red-400 flex items-center justify-center transition-colors disabled:opacity-50"
                      title={`Reset ${entry.user?.name} to 0 for this month`}
                      data-testid={`leaderboard-admin-reset-${entry.user?.id}`}
                      aria-label="Reset participant"
                    >
                      {resettingId === entry.user?.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          );
        })}
        
        {leaderboard.length === 0 && (
          <div className="p-8 text-center text-muted-foreground">
            No participants yet. Be the first to join!
          </div>
        )}
      </div>

      {logEditorUser && (
        <AdminDailyLogEditor
          challenge={challenge}
          user={logEditorUser}
          api={api}
          onClose={() => setLogEditorUser(null)}
          onSaved={onChanged}
        />
      )}
    </div>
  );
};
